import { useDebounceTextSearch } from '@gasbi/shared/common/hooks';
import { extractFormData } from '@gasbi/shared/common/utils/extractFormData';
import { buildGridifyQuery } from '@gasbi/shared/web/components/Filter';
import {
  makeFilterItemPrimitive,
  makeFilterItemTextSearch,
} from '@gasbi/shared/web/components/Filter/buildGridifyQuery/utils';
import { useTranslation } from 'react-i18next';

import { useGetMeteringNodesInfiniteQuery } from '@/features/meteringNode/meteringNodeApi';
import { useGetMeteringPointGasMeteringNodeByNodeIdQuery } from '@/features/meteringPoints/meteringPointsApi';
import { getUseQueryHookState } from '@/utils';

type FetchFormDataProps = Partial<{
  meteringPointId: string;
  meteringNodeId: string;
}>;

export const useFetchFormData = ({
  meteringPointId,
  meteringNodeId,
}: FetchFormDataProps) => {
  const { t } = useTranslation();

  const [
    meteringNodesFilterValue,
    meteringNodesSearch,
    setMeteringNodesSearch,
  ] = useDebounceTextSearch();

  const {
    data: meteringNodes,
    isLoading: meteringNodesIsLoading,
    isFetching: meteringNodesIsFetching,
    fetchNextPage: meteringNodesNext,
    hasNextPage: meteringNodesHasNextPage,
  } = useGetMeteringNodesInfiniteQuery(
    buildGridifyQuery([
      makeFilterItemPrimitive('meteringPointId', meteringPointId),
      makeFilterItemTextSearch(['name'], meteringNodesFilterValue),
    ]).build(),
    { skip: !meteringPointId },
  );

  const { data: meteringNode, ...nodeQuery } =
    useGetMeteringPointGasMeteringNodeByNodeIdQuery(
      {
        meteringPointId,
        meteringNodeId,
      },
      {
        skip: !meteringNodeId,
      },
    );

  const node = meteringNode
    ? {
        ...meteringNode,
        id: meteringNodeId,
        name: `Вузол обліку ${t(`MeteringNodeType.${meteringNode?.nodeType}`)}`,
      }
    : undefined;

  const isLoading = getUseQueryHookState('isLoading', [nodeQuery]);
  const isFetching = getUseQueryHookState('isFetching', [nodeQuery]);

  return {
    isLoading: isLoading || isFetching,
    node: node ? extractFormData([node]) : [],
    meteringNodes,
    meteringNodesIsLoading,
    meteringNodesIsFetching,
    meteringNodesNext,
    meteringNodesHasNextPage,
    meteringNodesSearch,
    setMeteringNodesSearch,
  };
};
